import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import {
  settingsQueries, monitoringQueries, vendorQueries, RiskTieringPolicy, WorkflowDefaultValues, WorkflowDefaults,
} from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import {
  Badge, Button, ConfirmDialog, CopyField, EmptyState, ErrorBlock, LoadingBlock, Notice, Palette, Panel, ReadOnlyHint,
  SaveBar, Segmented, SectionHeader, SettingRow, SettingsSectionProps, Toggle, apiError, fmtDate, fmtDateTime,
  inputStyle, palette, selectStyle, tableCell, tableHead, timeAgo,
} from './settingsUi';

/* Every non-integration section of the Settings page. Each renders its own header and reports unsaved edits to
 * the shell through setDirty, so switching section or leaving the page asks first. */

const PLAN_LABEL: Record<string, string> = { free: 'Free', pro: 'Pro', custom: 'Custom', enterprise: 'Enterprise' };
const PLAN_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' }> = {
  trial: { label: 'Trial', tone: 'warn' }, active: { label: 'Active', tone: 'ok' }, pro: { label: 'Active', tone: 'ok' },
  expired: { label: 'Expired', tone: 'danger' },
};

/* ─── General ──────────────────────────────────────────────────────────────────────────── */

export function GeneralSection({ isDark, isAdmin, notify, setDirty, onNavigate }: SettingsSectionProps & { onNavigate: (section: string) => void }) {
  const c = palette(isDark);
  const { user, refreshTenant } = useAuth();
  const overviewQuery = useQuery(settingsQueries.overview());
  const data = overviewQuery.data;
  const [name, setName] = useState<string | null>(null);   // null = untouched, shows the saved name

  const saved = data?.company_name ?? '';
  const draft = name ?? saved;
  const dirty = name !== null && name.trim() !== saved;
  useEffect(() => { setDirty?.(dirty); }, [dirty, setDirty]);

  const renameMutation = useMutation({
    mutationFn: async (company_name: string) => (await api.patch('/v1/settings/company', { company_name })).data,
    meta: { invalidates: [['settings'], ['my-risk'], ['vendors']] },
    onSuccess: async () => { await refreshTenant(); setName(null); notify('Company name updated.'); },
    onError: (err: any) => notify(apiError(err, 'Could not update the company name.'), 'error'),
  });

  if (overviewQuery.isPending) return <><SectionHeader c={c} title="General" /><LoadingBlock c={c} /></>;
  if (!data) return <><SectionHeader c={c} title="General" /><ErrorBlock c={c} message={apiError(overviewQuery.error, 'Could not load settings.')} onRetry={() => overviewQuery.refetch()} /></>;

  const nameError = !draft.trim() ? 'Company name is required' : draft.length > 200 ? 'Keep it under 200 characters' : '';

  return (
    <div>
      <SectionHeader c={c} title="General" description="Your workspace's identity and how vendors see you." />
      <div style={{ display: 'grid', gap: 16 }}>
        {isAdmin && <SetupChecklist c={c} vendorCount={data.vendor_count} teamCount={data.team.total} onNavigate={onNavigate} />}

        <Panel c={c} flush title="Workspace">
          <SettingRow c={c} first label="Company name"
            description="Shown to vendors on remediation requests, questionnaires and in the vendor portal, and on your reports.">
            {isAdmin ? (
              <div style={{ width: 320, maxWidth: '100%' }}>
                <input style={inputStyle(c, !!nameError && name !== null)} value={draft} maxLength={200}
                  aria-label="Company name" onChange={e => setName(e.target.value)} />
                {nameError && name !== null && <div style={{ fontSize: 11.5, color: c.danger, marginTop: 4 }}>{nameError}</div>}
              </div>
            ) : <span style={{ fontSize: 13.5, fontWeight: 600, color: c.ink }}>{saved}</span>}
          </SettingRow>
          <SettingRow c={c} label="Workspace ID" description="Quote this when you contact support.">
            <div style={{ width: 320, maxWidth: '100%' }}><CopyField c={c} value={user?.tenant_id || '—'} /></div>
          </SettingRow>
          <SettingRow c={c} label="Created">
            <span style={{ fontSize: 13, color: c.ink }}>{fmtDate(data.created_at)}</span>
          </SettingRow>
          <SettingRow c={c} label="Your access" description={isAdmin ? 'You can change every setting on this page.' : 'You can view these settings. Ask an admin to change them.'}>
            <Badge c={c} tone={isAdmin ? 'info' : 'muted'}>{isAdmin ? 'Admin' : 'View only'}</Badge>
          </SettingRow>
        </Panel>

        <Panel c={c} flush title="Your account" description="Personal settings live in your account menu (your avatar, top right).">
          <SettingRow c={c} first label="Name, password and session timeout" description="Change your own details, password, and how long you stay signed in on this device." />
          <SettingRow c={c} label="Notification bell" description="Turn your own in-app notifications on or off from the same menu." />
        </Panel>
      </div>

      <SaveBar c={c} visible={dirty} saving={renameMutation.isPending} canSave={!nameError}
        onDiscard={() => setName(null)} onSave={() => renameMutation.mutate(draft.trim())} />
    </div>
  );
}

export interface SetupStep { done: boolean; label: string; note: string; action: () => void; cta: string; optional?: boolean }

/** Workspace setup steps from real data. Shared by the General checklist and the Settings side rail (admins only:
 *  the AI provider list is an admin endpoint). */
export function useSetupSteps(vendorCount: number, teamCount: number, onNavigate: (s: string) => void, enabled = true) {
  const navigate = useNavigate();
  const aiQuery = useQuery({ ...settingsQueries.aiProviders(), enabled });
  const channelsQuery = useQuery({ ...monitoringQueries.channels(), enabled });
  const tieringQuery = useQuery({ ...settingsQueries.riskTiering(), enabled });
  const channelOn = (channelsQuery.data ?? []).some((ch: any) => { try { return !!JSON.parse(ch.config).enabled; } catch { return false; } });

  const steps: SetupStep[] = [
    { done: vendorCount > 0, label: 'Add your first vendor', note: 'Onboard a vendor to start scanning and monitoring it.', action: () => navigate('/vendors/add'), cta: 'Add vendor' },
    { done: teamCount > 1, label: 'Invite your team', note: 'Give risk owners and reviewers their own sign-in and role.', action: () => navigate('/users/add'), cta: 'Invite' },
    { done: (aiQuery.data?.length ?? 0) > 0, label: 'Connect an AI provider', note: 'Your own key powers summaries and descriptions.', action: () => onNavigate('ai-providers'), cta: 'Connect' },
    { done: channelOn, label: 'Turn on alert delivery', note: 'Get score drops and new critical findings by email, Slack or Teams.', action: () => navigate('/monitoring'), cta: 'Set up' },
    { done: !!tieringQuery.data?.is_customized, label: 'Review your risk tiering policy', note: 'Optional. Tune which data types make a vendor Critical or High.', action: () => onNavigate('risk-tiering'), cta: 'Review', optional: true },
  ];
  const loading = aiQuery.isPending || channelsQuery.isPending || tieringQuery.isPending;
  const required = steps.filter(st => !st.optional);
  const doneCount = required.filter(st => st.done).length;
  return { steps, loading, doneCount, requiredCount: required.length, next: required.find(st => !st.done) ?? null };
}

function SetupChecklist({ c, vendorCount, teamCount, onNavigate }: { c: Palette; vendorCount: number; teamCount: number; onNavigate: (s: string) => void }) {
  const { steps, loading, doneCount, requiredCount } = useSetupSteps(vendorCount, teamCount, onNavigate);
  if (loading) return null;
  if (doneCount === requiredCount) {
    return <Notice c={c} tone="ok" title="Your workspace is set up">Vendors, team, AI provider and alert delivery are all in place.</Notice>;
  }

  return (
    <Panel c={c} flush title="Finish setting up your workspace"
      badge={<Badge c={c} tone="info">{doneCount} of {requiredCount} done</Badge>}>
      {steps.map((s, i) => (
        <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: i ? `1px solid ${c.softLine}` : 'none', flexWrap: 'wrap' }}>
          <span aria-hidden style={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: s.done ? '#fff' : c.muted, background: s.done ? c.green : 'transparent',
            border: s.done ? 'none' : `1.5px solid ${c.line}`,
          }}>{s.done ? '✓' : i + 1}</span>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: s.done ? c.muted : c.ink, textDecoration: s.done ? 'line-through' : 'none' }}>{s.label}</div>
            {!s.done && <div style={{ fontSize: 12, color: c.slate, marginTop: 2 }}>{s.note}</div>}
          </div>
          {!s.done && <Button c={c} onClick={s.action} style={{ padding: '5px 12px', fontSize: 12 }}>{s.cta}</Button>}
        </div>
      ))}
    </Panel>
  );
}

/* ─── Plan & usage ─────────────────────────────────────────────────────────────────────── */

export function PlanSection({ isDark }: SettingsSectionProps) {
  const c = palette(isDark);
  const overviewQuery = useQuery(settingsQueries.overview());
  const data = overviewQuery.data;
  if (overviewQuery.isPending) return <><SectionHeader c={c} title="Plan & usage" /><LoadingBlock c={c} rows={2} /></>;
  if (!data) return <><SectionHeader c={c} title="Plan & usage" /><ErrorBlock c={c} message={apiError(overviewQuery.error, 'Could not load your plan.')} onRetry={() => overviewQuery.refetch()} /></>;

  const plan = data.plan;
  const status = PLAN_STATUS[plan.plan_status] || PLAN_STATUS.active;
  const unlimited = (max: number) => max == null || max < 0;
  const meter = (label: string, used: number, max: number, note: string) => {
    const pct = unlimited(max) ? 0 : Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
    const color = pct >= 100 ? c.danger : pct >= 80 ? c.amber : c.accent;
    return (
      <div style={{ padding: '16px 18px', border: `1px solid ${c.line}`, borderRadius: 10, background: c.surface }}>
        <div style={{ fontSize: 12, color: c.slate }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
          <span style={{ fontSize: 24, fontWeight: 700, color: c.ink }}>{used}</span>
          <span style={{ fontSize: 13, color: c.muted }}>/ {unlimited(max) ? 'Unlimited' : max}</span>
        </div>
        {!unlimited(max) && (
          <div style={{ height: 6, borderRadius: 3, background: c.chip, overflow: 'hidden', marginTop: 10 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
          </div>
        )}
        <div style={{ fontSize: 11.5, color: pct >= 100 ? c.danger : c.muted, marginTop: 8 }}>
          {pct >= 100 ? 'Limit reached — new additions are blocked until you upgrade or remove some.' : note}
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionHeader c={c} title="Plan & usage" description="What your subscription includes and how much of it you're using." />
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} title={`${plan.plan_name || PLAN_LABEL[plan.plan] || plan.plan} plan`} badge={<Badge c={c} tone={status.tone} dot>{status.label}</Badge>}
          description={plan.plan_status === 'trial'
            ? (plan.trial_days_remaining > 0
              ? `${plan.trial_days_remaining} day${plan.trial_days_remaining === 1 ? '' : 's'} left in your trial${plan.trial_end_date ? ` (ends ${fmtDate(plan.trial_end_date)})` : ''}.`
              : 'Your trial has ended.')
            : `Workspace created ${fmtDate(data.created_at)}.`}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {meter('Vendors', data.vendor_count, plan.max_vendors, plan.extra_vendors ? `Includes ${plan.extra_vendors} extra added to your plan. Archived vendors don't count.` : 'Active vendors count toward this. Archived ones don\'t.')}
            {meter('Team members', data.team.total, plan.max_users, plan.extra_users ? `Includes ${plan.extra_users} extra added to your plan, and pending invites.` : 'Everyone with a sign-in, including pending invites.')}
            <div style={{ padding: '16px 18px', border: `1px solid ${c.line}`, borderRadius: 10, background: c.surface }}>
              <div style={{ fontSize: 12, color: c.slate }}>Checks per scan</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: c.ink, marginTop: 6 }}>
                {plan.indicators_included ?? plan.max_indicators}
                {plan.all_indicators === false && plan.indicators_total ? <span style={{ fontSize: 14, fontWeight: 500, color: c.muted }}> of {plan.indicators_total}</span> : null}
              </div>
              <div style={{ fontSize: 11.5, color: c.muted, marginTop: 8 }}>
                {plan.all_indicators === false
                  ? 'Your plan runs a core set of checks. Upgrade to run every security, compliance and reputation check.'
                  : 'Every security, compliance and reputation check, run on every scan.'}
              </div>
            </div>
          </div>
        </Panel>
        <Notice c={c} tone="muted" title="Billing">
          Self-serve billing isn't available in the app yet. To change your plan or limits, contact your FluxWatch account team.
        </Notice>
      </div>
    </div>
  );
}

/* ─── Team & roles ─────────────────────────────────────────────────────────────────────── */

const ROLE_MATRIX: { role: string; view: boolean; edit: boolean; admin: boolean; note: string }[] = [
  { role: 'Viewer', view: true, edit: false, admin: false, note: 'Read and export' },
  { role: 'Risk analyst, risk manager, reviewer', view: true, edit: true, admin: false, note: 'Add and edit vendors, run scans, send requests' },
  { role: 'Admin', view: true, edit: true, admin: true, note: 'Delete data, manage the team and settings' },
];

export function TeamSection({ isDark }: SettingsSectionProps) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const overviewQuery = useQuery(settingsQueries.overview());
  const data = overviewQuery.data;

  const tile = (label: string, value: number, note: string, tone?: 'warn') => (
    <div style={{ padding: '14px 16px', border: `1px solid ${c.line}`, borderRadius: 10, background: c.surface }}>
      <div style={{ fontSize: 12, color: c.slate }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone === 'warn' && value ? c.amber : c.ink, marginTop: 4 }}>{overviewQuery.isPending ? '…' : value}</div>
      <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>{note}</div>
    </div>
  );
  const admins = data?.team.admin_members ?? [];
  const tick = (on: boolean) => <span style={{ color: on ? c.green : c.muted, fontWeight: 700 }}>{on ? '✓' : '—'}</span>;

  return (
    <div>
      <SectionHeader c={c} title="Team & roles" description="Who can sign in to this workspace, and what each role is allowed to do."
        actions={<><Button c={c} onClick={() => navigate('/users')}>Manage team</Button><Button c={c} kind="primary" onClick={() => navigate('/users/add')}>Invite member</Button></>} />
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {tile('Members', data?.team.total ?? 0, 'With a sign-in')}
          {tile('Active', data?.team.active ?? 0, 'Signed in at least once')}
          {tile('Invites pending', data?.team.pending ?? 0, 'Not accepted yet', 'warn')}
          {tile('Deactivated', data?.team.deactivated ?? 0, 'Kept for history')}
        </div>

        <Panel c={c} flush title="Administrators" badge={<Badge c={c} tone="info">{admins.length}</Badge>}
          description="Admins have full access: they can delete data, manage the team, and change every setting on this page.">
          {admins.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', borderTop: i ? `1px solid ${c.softLine}` : 'none' }}>
              <span style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, background: c.accentBg, color: c.accent,
              }}>{(m.name || m.email || '?').charAt(0).toUpperCase()}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{m.name || m.email}</div>
                {m.name && m.email && <div style={{ fontSize: 12, color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email}</div>}
              </div>
            </div>
          ))}
          {overviewQuery.isSuccess && admins.length === 0 && <EmptyState c={c} title="No active admins" body="Make at least one teammate an admin so someone can manage the workspace." />}
          {admins.length === 1 && (
            <div style={{ padding: '0 20px 14px' }}>
              <Notice c={c} tone="warn">Only one admin. If they leave or lose access, no one can manage the team or settings — consider a second admin.</Notice>
            </div>
          )}
        </Panel>

        <Panel c={c} flush title="Roles & permissions" description="Enforced by the platform on every request, not just hidden in the screens.">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr>{['Role', 'View & export', 'Add & edit', 'Admin', ''].map(h => <th key={h} style={tableHead(c)}>{h}</th>)}</tr></thead>
              <tbody>
                {ROLE_MATRIX.map(r => (
                  <tr key={r.role}>
                    <td style={{ ...tableCell(c), fontWeight: 600 }}>{r.role}</td>
                    <td style={tableCell(c)}>{tick(r.view)}</td>
                    <td style={tableCell(c)}>{tick(r.edit)}</td>
                    <td style={tableCell(c)}>{tick(r.admin)}</td>
                    <td style={{ ...tableCell(c), color: c.slate }}>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ─── Risk tiering ─────────────────────────────────────────────────────────────────────── */

const SENSITIVITY_LABEL: Record<string, { label: string; note: string }> = {
  none: { label: 'No sensitive data', note: 'Public or no data' },
  internal: { label: 'Internal data only', note: 'Business data, no personal data' },
  pii: { label: 'Customer PII', note: 'Names, emails, IDs of your customers' },
  financial: { label: 'Financial or payment data', note: 'Card, bank or payment data' },
  regulated: { label: 'Regulated data', note: 'Health, government or other regulated data' },
};
const TIER_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info'> = { Low: 'ok', Medium: 'warn', High: 'danger', Critical: 'danger' };
const CYCLE_CHOICES = [3, 6, 9, 12, 18, 24, 36, 48, 60];

function TierPill({ c, tier }: { c: Palette; tier: string }) {
  const colors: Record<string, string> = { Low: c.green, Medium: c.amber, High: c.isDark ? '#fb923c' : '#EA580C', Critical: c.danger };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: colors[tier] }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors[tier] }} />{tier}
    </span>
  );
}

export function RiskTieringSection({ isDark, isAdmin, notify, setDirty }: SettingsSectionProps) {
  const c = palette(isDark);
  const policyQuery = useQuery(settingsQueries.riskTiering());
  const data = policyQuery.data;
  const [tiers, setTiers] = useState<Record<string, string> | null>(null);
  const [months, setMonths] = useState<Record<string, number> | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const curTiers = tiers ?? data?.sensitivity_tiers ?? {};
  const curMonths = months ?? data?.review_cycle_months ?? {};
  const dirty = !!data && (JSON.stringify(curTiers) !== JSON.stringify(data.sensitivity_tiers) || JSON.stringify(curMonths) !== JSON.stringify(data.review_cycle_months));
  useEffect(() => { setDirty?.(dirty); }, [dirty, setDirty]);
  const discard = () => { setTiers(null); setMonths(null); };

  const saveMutation = useMutation({
    mutationFn: async () => (await api.patch('/v1/settings/risk-tiering', { sensitivity_tiers: curTiers, review_cycle_months: curMonths })).data as RiskTieringPolicy,
    meta: { invalidates: [['settings'], ['vendors'], ['vendor-requests']] },
    onSuccess: () => { discard(); notify('Risk tiering policy saved.'); },
    onError: (err: any) => notify(apiError(err, 'Could not save the policy.'), 'error'),
  });
  const resetMutation = useMutation({
    mutationFn: async () => (await api.post('/v1/settings/risk-tiering/reset')).data as RiskTieringPolicy,
    meta: { invalidates: [['settings'], ['vendors'], ['vendor-requests']] },
    onSuccess: () => { discard(); setConfirmReset(false); notify('Risk tiering reset to platform defaults.'); },
    onError: (err: any) => notify(apiError(err, 'Could not reset the policy.'), 'error'),
  });

  const header = (
    <SectionHeader c={c} title="Risk tiering"
      description="How a vendor's inherent risk tier is decided from the data it handles, and how often each tier is re-reviewed. Used on Pre Onboarding requests and each vendor's Profile. It never changes a scan score."
      actions={isAdmin && data?.is_customized && <Button c={c} onClick={() => setConfirmReset(true)}>Reset to defaults</Button>} />
  );
  if (policyQuery.isPending) return <>{header}<LoadingBlock c={c} rows={2} /></>;
  if (!data) return <>{header}<ErrorBlock c={c} message={apiError(policyQuery.error, 'Could not load the policy.')} onRetry={() => policyQuery.refetch()} /></>;

  const changed = (s: string) => curTiers[s] !== data.default_sensitivity_tiers[s];
  return (
    <div>
      {header}
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Data handled → inherent tier"
          badge={<Badge c={c} tone={data.is_customized ? 'info' : 'muted'}>{data.is_customized ? 'Customized' : 'Platform defaults'}</Badge>}
          description="A vendor's tier is the highest of its data, business-criticality and system-access factors; this table sets the data factor.">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr>{['Data this vendor handles', 'Inherent tier', 'Re-reviewed every'].map(h => <th key={h} style={tableHead(c)}>{h}</th>)}</tr></thead>
              <tbody>
                {data.sensitivity_options.map(s => (
                  <tr key={s}>
                    <td style={tableCell(c)}>
                      <div style={{ fontWeight: 600 }}>{SENSITIVITY_LABEL[s]?.label || s}</div>
                      <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>{SENSITIVITY_LABEL[s]?.note}</div>
                    </td>
                    <td style={tableCell(c)}>
                      {isAdmin ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <select aria-label={`Tier for ${SENSITIVITY_LABEL[s]?.label || s}`} style={{ ...selectStyle(c), minWidth: 120 }} value={curTiers[s]}
                            onChange={e => setTiers({ ...curTiers, [s]: e.target.value })}>
                            {data.tier_options.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {changed(s) && <span style={{ fontSize: 11, color: c.muted }}>default {data.default_sensitivity_tiers[s]}</span>}
                        </span>
                      ) : <TierPill c={c} tier={curTiers[s]} />}
                    </td>
                    <td style={{ ...tableCell(c), color: c.slate }}>{curMonths[curTiers[s]]} months</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel c={c} flush title="Review cadence by tier" description="How often a vendor of each tier should be formally re-assessed. Sets the next review date suggested on its Profile.">
          {[...data.tier_options].reverse().map((t, i) => (
            <SettingRow key={t} c={c} first={i === 0} label={<TierPill c={c} tier={t} />}
              description={curMonths[t] !== data.default_review_cycle_months[t] ? `Platform default: every ${data.default_review_cycle_months[t]} months` : undefined}>
              {isAdmin ? (
                <select aria-label={`Review cadence for ${t}`} style={{ ...selectStyle(c), minWidth: 150 }} value={curMonths[t]}
                  onChange={e => setMonths({ ...curMonths, [t]: Number(e.target.value) })}>
                  {Array.from(new Set([...CYCLE_CHOICES, curMonths[t]])).sort((a, b) => a - b).map(m => <option key={m} value={m}>Every {m} months</option>)}
                </select>
              ) : <span style={{ fontSize: 13, color: c.ink }}>Every {curMonths[t]} months</span>}
            </SettingRow>
          ))}
        </Panel>
        {!isAdmin && <ReadOnlyHint c={c} />}
      </div>

      <SaveBar c={c} visible={dirty} saving={saveMutation.isPending} onDiscard={discard} onSave={() => saveMutation.mutate()}
        message="Unsaved changes to your tiering policy" />
      <ConfirmDialog c={c} open={confirmReset} title="Reset risk tiering to platform defaults?" confirmLabel="Reset" busy={resetMutation.isPending}
        body="Every data type goes back to its default tier and every tier to its default review cadence. Vendor tiers on Pre Onboarding and Profile update right away."
        onConfirm={() => resetMutation.mutate()} onCancel={() => setConfirmReset(false)} />
    </div>
  );
}

/* ─── Workflow defaults ────────────────────────────────────────────────────────────────── */

const REMINDER_LABEL = (d: number) => (d === 0 ? 'No reminder' : d === 7 ? '1 week before' : d === 14 ? '2 weeks before' : `${d} day${d === 1 ? '' : 's'} before`);

export function WorkflowDefaultsSection({ isDark, isAdmin, notify, setDirty }: SettingsSectionProps) {
  const c = palette(isDark);
  const defaultsQuery = useQuery(settingsQueries.defaults());
  const data = defaultsQuery.data;
  const [draft, setDraft] = useState<Partial<WorkflowDefaultValues>>({});
  const [confirmReset, setConfirmReset] = useState(false);

  const values: WorkflowDefaultValues | undefined = data ? { ...data.values, ...draft } : undefined;
  const changedKeys = data ? (Object.keys(draft) as (keyof WorkflowDefaultValues)[]).filter(k => draft[k] !== data.values[k]) : [];
  const dirty = changedKeys.length > 0;
  useEffect(() => { setDirty?.(dirty); }, [dirty, setDirty]);
  const set = <K extends keyof WorkflowDefaultValues>(k: K, v: WorkflowDefaultValues[K]) => setDraft(d => ({ ...d, [k]: v }));

  const saveMutation = useMutation({
    mutationFn: async () => (await api.patch('/v1/settings/defaults', { values: Object.fromEntries(changedKeys.map(k => [k, draft[k]])) })).data as WorkflowDefaults,
    meta: { invalidates: [['settings']] },
    onSuccess: () => { setDraft({}); notify('Workflow defaults saved.'); },
    onError: (err: any) => notify(apiError(err, 'Could not save the defaults.'), 'error'),
  });
  const resetMutation = useMutation({
    mutationFn: async () => (await api.post('/v1/settings/defaults/reset')).data as WorkflowDefaults,
    meta: { invalidates: [['settings']] },
    onSuccess: () => { setDraft({}); setConfirmReset(false); notify('Workflow defaults reset.'); },
    onError: (err: any) => notify(apiError(err, 'Could not reset the defaults.'), 'error'),
  });

  const header = (
    <SectionHeader c={c} title="Workflow defaults"
      description="The starting values your team's forms use, so everyone works the same way. People can still change them case by case."
      actions={isAdmin && data?.is_customized && <Button c={c} onClick={() => setConfirmReset(true)}>Reset to defaults</Button>} />
  );
  if (defaultsQuery.isPending) return <>{header}<LoadingBlock c={c} rows={2} /></>;
  if (!data || !values) return <>{header}<ErrorBlock c={c} message={apiError(defaultsQuery.error, 'Could not load workflow defaults.')} onRetry={() => defaultsQuery.refetch()} /></>;

  const usedIn = (where: string, key: keyof WorkflowDefaultValues) => (
    <>
      Used in {where}.
      {values[key] !== data.defaults[key] && <span style={{ color: c.muted }}> Platform default: {
        key === 'screen_on_request' ? (data.defaults[key] ? 'on' : 'off')
          : key === 'remediation_reminder_days' ? REMINDER_LABEL(data.defaults[key] as number).toLowerCase()
            : key === 'access_review_months' ? `${data.defaults[key]} months` : `${data.defaults[key]} days`}.</span>}
    </>
  );
  const choice = (key: 'renewal_notice_days' | 'remediation_due_days' | 'remediation_reminder_days' | 'access_review_months', label: (v: number) => string) => (
    isAdmin ? (
      <select aria-label={key} style={{ ...selectStyle(c), minWidth: 190 }} value={values[key]} onChange={e => set(key, Number(e.target.value))}>
        {data.choices[key].map(v => <option key={v} value={v}>{label(v)}</option>)}
      </select>
    ) : <span style={{ fontSize: 13, color: c.ink }}>{label(values[key])}</span>
  );

  return (
    <div>
      {header}
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Vendor onboarding">
          <SettingRow c={c} first label="Contract renewal notice" description={usedIn('Add Vendor → Contract', 'renewal_notice_days')}>
            {choice('renewal_notice_days', v => `${v} days before expiry`)}
          </SettingRow>
          <SettingRow c={c} label="Screen new requests automatically"
            description={usedIn('Pre Onboarding → New request: runs the screening scan as soon as a request is filed', 'screen_on_request')}>
            {isAdmin ? <Toggle c={c} label="Screen new requests automatically" checked={values.screen_on_request} onChange={v => set('screen_on_request', v)} />
              : <Badge c={c} tone={values.screen_on_request ? 'ok' : 'muted'}>{values.screen_on_request ? 'On' : 'Off'}</Badge>}
          </SettingRow>
        </Panel>

        <Panel c={c} flush title="Remediation requests">
          <SettingRow c={c} first label="Due date" description={usedIn('Request remediation → Details', 'remediation_due_days')}>
            {choice('remediation_due_days', v => `${v} days after sending`)}
          </SettingRow>
          <SettingRow c={c} label="Reminder to the vendor" description={usedIn('Request remediation → Details (an email to the vendor before the due date)', 'remediation_reminder_days')}>
            {choice('remediation_reminder_days', REMINDER_LABEL)}
          </SettingRow>
        </Panel>

        <Panel c={c} flush title="Vendor access">
          <SettingRow c={c} first label="Access review interval" description={usedIn('Vendor → Access, when recording a new access grant', 'access_review_months')}>
            {choice('access_review_months', v => `Every ${v} months`)}
          </SettingRow>
        </Panel>
        {!isAdmin && <ReadOnlyHint c={c} />}
      </div>

      <SaveBar c={c} visible={dirty} saving={saveMutation.isPending} onDiscard={() => setDraft({})} onSave={() => saveMutation.mutate()} />
      <ConfirmDialog c={c} open={confirmReset} title="Reset workflow defaults?" confirmLabel="Reset" busy={resetMutation.isPending}
        body="Every default on this page goes back to the platform value. Existing vendors, requests and access records are not changed."
        onConfirm={() => resetMutation.mutate()} onCancel={() => setConfirmReset(false)} />
    </div>
  );
}

/* ─── Scoring methodology (read-only, for auditability) ────────────────────────────────── */

const BANDS = [
  { label: 'Low', range: '80 – 100', tone: 'ok' as const },
  { label: 'Medium', range: '60 – 79', tone: 'warn' as const },
  { label: 'High', range: '40 – 59', tone: 'danger' as const },
  { label: 'Critical', range: '0 – 39', tone: 'danger' as const },
];

export function ScoringSection({ isDark }: SettingsSectionProps) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const categoriesQuery = useQuery(vendorQueries.riskCategories());
  const categories = categoriesQuery.data ?? [];

  return (
    <div>
      <SectionHeader c={c} title="Scoring methodology"
        description="How a vendor's 0–100 security score is calculated. The method is the same for every vendor, so scores stay comparable across vendors and over time — this page documents it for your auditors." />
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} title="Risk bands" description="The score decides the risk level shown everywhere in FluxWatch.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            {BANDS.map(b => (
              <div key={b.label} style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${c.line}` }}>
                <TierPill c={c} tier={b.label} />
                <div style={{ fontSize: 18, fontWeight: 700, color: c.ink, marginTop: 6 }}>{b.range}</div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel c={c} title="How the score is built">
          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 10, fontSize: 13, color: c.slate, lineHeight: 1.55 }}>
            <li><b style={{ color: c.ink }}>Every scan runs the same security checks</b> on the vendor's domains — DNS, email security, TLS, web security, exposed services, threat intelligence, reputation and more.</li>
            <li><b style={{ color: c.ink }}>Each finding carries a severity</b> (Critical, High, Medium or Low). Clean checks have no impact.</li>
            <li><b style={{ color: c.ink }}>Findings roll up into {categories.length || 14} risk categories</b>, each scored 0–100.</li>
            <li><b style={{ color: c.ink }}>Category scores are combined into the overall score</b>, with categories that have more serious findings counting for more.</li>
          </ol>
        </Panel>

        <Panel c={c} flush title="Scored categories" badge={<Badge c={c}>{categories.length}</Badge>}
          description="Company profile and funding information are shown on vendors but never scored.">
          {categoriesQuery.isPending ? <div style={{ padding: 20 }}><LoadingBlock c={c} rows={1} /></div> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '14px 20px' }}>
              {categories.map(cat => (
                <span key={cat} style={{ fontSize: 12.5, padding: '5px 10px', borderRadius: 99, background: c.chip, color: c.ink }}>{cat}</span>
              ))}
            </div>
          )}
        </Panel>

        <Panel c={c} title="Related" flush>
          <SettingRow c={c} first label="Inherent risk tiers" description="The separate, business-driven tier set by the data a vendor handles.">
            <Button c={c} kind="ghost" onClick={() => navigate('/settings?section=risk-tiering')}>Risk tiering →</Button>
          </SettingRow>
        </Panel>
      </div>
    </div>
  );
}

/* ─── Alert delivery ───────────────────────────────────────────────────────────────────── */

const CHANNEL_TYPE: Record<string, string> = { email: 'Email', slack: 'Slack', teams: 'Microsoft Teams', webhook: 'Webhook' };

export function NotificationsSection({ isDark, isAdmin, notify }: SettingsSectionProps) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const channelsQuery = useQuery(monitoringQueries.channels());
  const rulesQuery = useQuery(monitoringQueries.rules());
  const briefingQuery = useQuery(settingsQueries.portfolioBriefing());
  const parse = (ch: any) => { try { return JSON.parse(ch.config) || {}; } catch { return {}; } };
  const channels = channelsQuery.data ?? [];
  const enabledRules = (rulesQuery.data ?? []).filter((r: any) => !!r.is_active).length;

  const briefingMutation = useMutation({
    mutationFn: async (enabled: boolean) => (await api.patch('/v1/settings/portfolio-briefing', { enabled })).data as { enabled: boolean },
    meta: { invalidates: [['settings', 'portfolio-briefing']] },
    onError: (err: any) => notify?.(apiError(err, 'Could not change the portfolio briefing setting.'), 'error'),
  });

  return (
    <div>
      <SectionHeader c={c} title="Alert delivery"
        description="Where monitoring alerts are sent when a vendor's score drops, a critical finding appears or a certificate is about to expire."
        actions={<Button c={c} kind="primary" onClick={() => navigate('/monitoring')}>Open Alerting</Button>} />
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Delivery channels" description="Turn channels on, set their minimum severity and send a test from the Alerting page → Delivery.">
          {channelsQuery.isPending ? <div style={{ padding: 20 }}><LoadingBlock c={c} rows={1} /></div>
            : channels.length === 0 ? <EmptyState c={c} title="No channels yet" body="Add email, Slack, Teams or a webhook to start receiving alerts."
              action={<Button c={c} onClick={() => navigate('/monitoring')}>Set up delivery</Button>} />
              : channels.map((ch: any, i: number) => {
                const cfg = parse(ch);
                return (
                  <SettingRow key={ch.id} c={c} first={i === 0} label={ch.name || CHANNEL_TYPE[ch.type] || ch.type}
                    description={`${CHANNEL_TYPE[ch.type] || ch.type}${cfg.min_severity ? ` · ${cfg.min_severity} and above` : ''}`}>
                    <Badge c={c} tone={cfg.enabled ? 'ok' : 'muted'} dot>{cfg.enabled ? 'On' : 'Off'}</Badge>
                  </SettingRow>
                );
              })}
        </Panel>
        <Panel c={c} flush title="Alert rules">
          <SettingRow c={c} first label="Active rules" description="Which events raise an alert, for which vendors, and how often they repeat.">
            <span style={{ fontSize: 13, color: c.ink, fontWeight: 600 }}>{rulesQuery.isPending ? '…' : enabledRules}</span>
            <Button c={c} kind="ghost" onClick={() => navigate('/monitoring')}>Manage →</Button>
          </SettingRow>
          <SettingRow c={c} label="In-app notifications" description="Everyone on the team sees alerts in the bell at the top of the page. Each person can turn their bell off from their account menu." />
        </Panel>
        <Panel c={c} flush title="Portfolio briefing" description="A weekly digest of the whole portfolio — average score, biggest movers, overdue scans, upcoming renewals and remediation backlog — sent through the same channels above.">
          <SettingRow c={c} first label="Weekly briefing" description="Sent every Monday morning. On by default.">
            {briefingQuery.isPending ? <span style={{ fontSize: 13, color: c.muted }}>…</span>
              : isAdmin ? <Toggle c={c} label="Weekly portfolio briefing" checked={!!briefingQuery.data?.enabled}
                  onChange={v => briefingMutation.mutate(v)} disabled={briefingMutation.isPending} />
                : <Badge c={c} tone={briefingQuery.data?.enabled ? 'ok' : 'muted'} dot>{briefingQuery.data?.enabled ? 'On' : 'Off'}</Badge>}
          </SettingRow>
        </Panel>
      </div>
    </div>
  );
}

/* ─── Security ─────────────────────────────────────────────────────────────────────────── */

export function SecuritySection({ isDark, isAdmin, onNavigate }: SettingsSectionProps & { onNavigate: (section: string) => void }) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const overviewQuery = useQuery(settingsQueries.overview());
  const admins = overviewQuery.data?.team.admins;

  const ctl = (label: string, description: string, status: { text: string; tone: 'ok' | 'muted' | 'warn' }, first = false, action?: React.ReactNode) => (
    <SettingRow c={c} first={first} label={label} description={description}>
      <Badge c={c} tone={status.tone} dot>{status.text}</Badge>{action}
    </SettingRow>
  );

  return (
    <div>
      <SectionHeader c={c} title="Security" description="The controls that protect this workspace. Enforced controls are checked on every request." />
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Authentication">
          {ctl('Passwords', 'At least 8 characters. Each person changes theirs from their account menu.', { text: 'Enforced', tone: 'ok' }, true)}
          {ctl('Sign-in rate limiting', 'Sign-in: 10 attempts a minute per IP address. Sign-up and password reset: 5 every 5 minutes.', { text: 'Enforced', tone: 'ok' })}
          {ctl('Session timeout', 'Each person sets how long they stay signed in on a device, from their account menu.', { text: 'Per person', tone: 'ok' })}
          {ctl('Two-factor authentication', 'Not available in FluxWatch yet.', { text: 'Not available', tone: 'muted' })}
          {ctl('Single sign-on (SAML / OIDC)', 'Not available in FluxWatch yet.', { text: 'Not available', tone: 'muted' })}
        </Panel>

        <Panel c={c} flush title="Access & keys">
          {ctl('Role-based access', `Viewer, editor and admin permissions are checked by the server on every request.${admins != null ? ` ${admins} admin${admins === 1 ? '' : 's'} in this workspace.` : ''}`,
            { text: 'Enforced', tone: 'ok' }, true, <Button c={c} kind="ghost" onClick={() => onNavigate('team')}>Review →</Button>)}
          {ctl('AI and financial provider keys', 'Your own provider keys are encrypted at rest with a key separate from the one that signs sessions.', { text: 'Encrypted', tone: 'ok' })}
          {ctl('Workspace isolation', 'Every record belongs to one workspace; live updates can only see their own workspace.', { text: 'Enforced', tone: 'ok' })}
        </Panel>

        <Panel c={c} flush title="Monitoring & audit">
          <SettingRow c={c} first label="Audit log" description="Every sign-in and every vendor, team, remediation, access and settings change is recorded with who did it and when.">
            <Button c={c} onClick={() => navigate('/audit-logs')}>Open audit log</Button>
          </SettingRow>
          {isAdmin && (
            <SettingRow c={c} label="Settings change history" description="Just the configuration changes: workspace, team, integrations and alerting.">
              <Button c={c} kind="ghost" onClick={() => onNavigate('history')}>View →</Button>
            </SettingRow>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ─── Change history ───────────────────────────────────────────────────────────────────── */

const ACTION_LABEL: Record<string, string> = {
  'settings.company_renamed': 'Company name changed', 'settings.risk_tiering_updated': 'Risk tiering policy updated',
  'settings.risk_tiering_reset': 'Risk tiering reset to defaults', 'settings.defaults_updated': 'Workflow defaults changed',
  'settings.defaults_reset': 'Workflow defaults reset', 'tenant.upgrade': 'Plan changed',
  'user.create': 'Teammate invited', 'user.update': 'Teammate updated', 'user.update_restrictions': 'Access restrictions changed',
  'user.activate': 'Teammate reactivated', 'user.deactivate': 'Teammate deactivated', 'user.permanent_delete': 'Teammate removed',
  'user.resend_invite': 'Invite re-sent', 'user.transfer_vendors': 'Vendors transferred',
  'ai_provider.configured': 'AI provider key saved', 'ai_provider.deleted': 'AI provider key removed',
  'api_key.created': 'API key created', 'api_key.revoked': 'API key revoked', 'api_key.deleted': 'API key deleted',
  'financial_provider.configured': 'Financial provider connected', 'financial_provider.deleted': 'Financial provider removed',
  'config.updated': 'Alerting configuration changed',
};
const GROUP_LABEL: Record<string, string> = { all: 'All', workspace: 'Workspace', team: 'Team', integrations: 'Integrations', alerting: 'Alerting' };
const humanize = (action: string) => ACTION_LABEL[action] || action.replace(/[._]/g, ' ').replace(/^\w/, ch => ch.toUpperCase());

export function HistorySection({ isDark, isAdmin }: SettingsSectionProps) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const [group, setGroup] = useState('all');
  const [q, setQ] = useState('');
  const historyQuery = useQuery({ ...settingsQueries.history(group), enabled: isAdmin });
  const entries = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = historyQuery.data?.entries ?? [];
    return needle ? all.filter(e => `${humanize(e.action)} ${e.details ?? ''} ${e.user_name ?? ''} ${e.user_email ?? ''}`.toLowerCase().includes(needle)) : all;
  }, [historyQuery.data, q]);

  const byDay = useMemo(() => {
    const out: { day: string; items: typeof entries }[] = [];
    entries.forEach(e => {
      const day = new Date(e.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(e); else out.push({ day, items: [e] });
    });
    return out;
  }, [entries]);

  return (
    <div>
      <SectionHeader c={c} title="Change history" description="Who changed this workspace's configuration, and when. Kept as part of your audit trail."
        actions={<Button c={c} onClick={() => navigate('/audit-logs')}>Full audit log</Button>} />
      {!isAdmin ? <Notice c={c} tone="muted" title="Only admins can view the change history" /> : (
        <Panel c={c} flush
          title={<Segmented c={c} value={group} onChange={setGroup} options={['all', 'workspace', 'team', 'integrations', 'alerting'].map(g => ({ value: g, label: GROUP_LABEL[g] }))} />}
          actions={<input aria-label="Search change history" placeholder="Search changes…" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle(c), width: 220 }} />}>
          {historyQuery.isPending ? <div style={{ padding: 20 }}><LoadingBlock c={c} rows={2} /></div>
            : historyQuery.isError ? <EmptyState c={c} title="Couldn't load the history" body={apiError(historyQuery.error, '')} action={<Button c={c} onClick={() => historyQuery.refetch()}>Try again</Button>} />
              : entries.length === 0 ? <EmptyState c={c} title={q ? 'No changes match your search' : 'No changes recorded yet'} body={q ? undefined : 'Changes to settings, the team and integrations will appear here.'} />
                : byDay.map(d => (
                  <div key={d.day}>
                    <div style={{ padding: '8px 20px', fontSize: 11.5, fontWeight: 600, color: c.slate, background: c.surface2, borderTop: `1px solid ${c.line}`, borderBottom: `1px solid ${c.line}` }}>{d.day}</div>
                    {d.items.map((e, i) => (
                      <div key={e.id} style={{ display: 'flex', gap: 12, padding: '12px 20px', borderTop: i ? `1px solid ${c.softLine}` : 'none', alignItems: 'flex-start' }}>
                        <span style={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11.5, fontWeight: 700, background: c.chip, color: c.slate,
                        }}>{(e.user_name || e.user_email || '?').charAt(0).toUpperCase()}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{humanize(e.action)}</span>
                            <Badge c={c}>{GROUP_LABEL[e.group] || e.group}</Badge>
                          </div>
                          {e.details && <div style={{ fontSize: 12, color: c.slate, marginTop: 3, lineHeight: 1.5, wordBreak: 'break-word' }}>{e.details}</div>}
                          <div style={{ fontSize: 11.5, color: c.muted, marginTop: 4 }}>
                            {e.user_name || e.user_email || 'System'} · <span title={fmtDateTime(e.created_at)}>{timeAgo(e.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
        </Panel>
      )}
    </div>
  );
}

/* ─── Data & exports ───────────────────────────────────────────────────────────────────── */

export function DataSection({ isDark, isAdmin, onNavigate }: SettingsSectionProps & { onNavigate: (section: string) => void }) {
  const c = palette(isDark);
  const navigate = useNavigate();
  const row = (label: string, description: string, cta: string, onClick: () => void, first = false) => (
    <SettingRow c={c} first={first} label={label} description={description}><Button c={c} onClick={onClick}>{cta}</Button></SettingRow>
  );
  return (
    <div>
      <SectionHeader c={c} title="Data & exports" description="Getting your data out of FluxWatch, and how long it's kept." />
      <div style={{ display: 'grid', gap: 16 }}>
        <Panel c={c} flush title="Exports">
          {row('Vendor portfolio (CSV)', 'Every vendor with score, risk level, owner, lifecycle and contract dates. Select rows on Vendors and export.', 'Open Vendors', () => navigate('/vendors'), true)}
          {row('Remediation requests (CSV)', 'Every request with its status, due date and open risks.', 'Open Remediation', () => navigate('/remediation'))}
          {row('Reports (PDF)', 'Portfolio and vendor reports, and side-by-side comparisons.', 'Open Reports', () => navigate('/reports'))}
          {row('Scan results', 'Every scan\'s full, indicator-by-indicator findings.', 'Open Vendors', () => navigate('/vendors'))}
          {row('Audit trail', 'Every recorded action in the workspace.', 'Open audit log', () => navigate('/audit-logs'))}
        </Panel>
        <Panel c={c} flush title="Retention">
          <SettingRow c={c} first label="Scan history" description="Every completed scan is kept so you can compare scores over time. Permanently deleting a vendor deletes its scans too." />
          <SettingRow c={c} label="Archived vendors" description="Archiving keeps a vendor's history and can be undone. Permanent deletion is admin-only and can't be undone." />
          <SettingRow c={c} label="Audit log" description="Kept for the life of the workspace." />
        </Panel>
      </div>
    </div>
  );
}
